import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  OrganizationInvitationPrompt,
  AdminAuthPanelModel,
  AuthErrorCode,
  AuthMode,
  AuthUser,
  FeedbackTone,
  FlowNotice,
  PasswordRule,
} from "../types/admin";
import { TranslationKey } from "../../shared/i18n/i18n";
import {
  acceptAdminOrganizationInvitation,
  acceptLatestTermsOfUse,
  completeAdminTwoFactor,
  declineAdminOrganizationInvitation,
  fetchAdminAuthStatus,
  fetchLatestTermsOfUse,
  fetchLatestTermsOfUseSummary,
  previewAdminOrganizationInvitation,
  registerUnauthorizedResponseHandler,
  requestAdminPasswordReset,
  resendAdminVerification,
  resetAdminPassword,
  signInAdmin,
  signOutAdmin,
  signUpAdmin,
  verifyAdminEmail,
} from "../../shared/requests";
import { useI18n } from "../../shared/i18n/i18n";
import { TURNSTILE_SITE_KEY } from "../../shared/config/runtimeConfig";

const passwordPattern = "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z\\d\\s]).{15,}$";
const AUTH_ERROR_TRANSLATION_KEYS: Record<AuthErrorCode, TranslationKey> = {
  ORGANIZATION_DISABLED: "admin.auth.error.organizationDisabled",
  EMAIL_VERIFICATION_REQUIRED: "admin.auth.error.emailVerificationRequired",
  INVALID_CREDENTIALS: "admin.auth.error.invalidCredentials",
  PASSWORD_RESET_INVALID: "admin.auth.error.passwordResetInvalid",
  ROBOT_DETECTION_FAILED: "admin.auth.error.robotDetectionFailed",
  USER_ALREADY_EXISTS: "admin.auth.error.userAlreadyExists",
  VERIFY_EMAIL_INVALID: "admin.auth.error.verifyEmailInvalid",
};

const getInitialModeFromUrl = (): AuthMode => {
  const mode = new URL(window.location.href).searchParams.get("mode");
  if (
    mode === "organization-invitation-accept"
    || mode === "organization-invitation-decline"
    || mode === "sign-up"
    || mode === "forgot-password"
    || mode === "forgot-password-reset"
    || mode === "verify-email"
    || mode === "sign-in"
  ) {
    return mode;
  }
  return "sign-in";
};

const getInitialTokenFromUrl = (): string => new URL(window.location.href).searchParams.get("token") ?? "";
const replaceUrlForMode = (mode: AuthMode) => {
  const url = new URL(window.location.href);
  if (mode === "sign-in") {
    url.searchParams.delete("mode");
    url.searchParams.delete("token");
  } else {
    url.searchParams.set("mode", mode);
    if (
      mode !== "organization-invitation-accept"
      && mode !== "organization-invitation-decline"
      && mode !== "forgot-password-reset"
      && mode !== "verify-email"
    ) {
      url.searchParams.delete("token");
    }
  }
  window.history.replaceState({}, "", url.toString());
};

export const useAdminAuth = (
  t: (key: TranslationKey) => string,
  onOrganizationInvitationAccepted: () => void = () => undefined,
) => {
  const { language } = useI18n();
  const passwordRequirements = t("admin.password.requirements");
  const getPasswordRules = useCallback((password: string): PasswordRule[] => [
    { id: "length", label: t("admin.password.rule.length"), met: password.length >= 15 },
    { id: "uppercase", label: t("admin.password.rule.uppercase"), met: /[A-Z]/.test(password) },
    { id: "lowercase", label: t("admin.password.rule.lowercase"), met: /[a-z]/.test(password) },
    { id: "number", label: t("admin.password.rule.number"), met: /\d/.test(password) },
    { id: "special", label: t("admin.password.rule.special"), met: /[^A-Za-z\d\s]/.test(password) }
  ], [t]);

  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>(getInitialModeFromUrl());
  const [organizationInvitationPrompt, setOrganizationInvitationPrompt] = useState<OrganizationInvitationPrompt | null>(null);
  const [flowNotice, setFlowNotice] = useState<FlowNotice | null>(null);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotPasswordTurnstileToken, setForgotPasswordTurnstileToken] = useState("");
  const [forgotPasswordTurnstileResetKey, setForgotPasswordTurnstileResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState(getInitialTokenFromUrl());
  const [latestTermsOfUseAccepted, setLatestTermsOfUseAccepted] = useState(false);
  const [isAcceptancePopupOpen, setIsAcceptancePopupOpen] = useState(false);
  const [isUpcomingTermsBannerDismissed, setIsUpcomingTermsBannerDismissed] = useState(false);
  const [isTermsOfUseOpen, setIsTermsOfUseOpen] = useState(false);
  const [latestTermsOfUseSummary, setLatestTermsOfUseSummary] = useState<Awaited<ReturnType<typeof fetchLatestTermsOfUseSummary>>["payload"] | null>(null);
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signUpAcceptedTerms, setSignUpAcceptedTerms] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpReceiveProductUpdates, setSignUpReceiveProductUpdates] = useState(false);
  const [signUpTurnstileToken, setSignUpTurnstileToken] = useState("");
  const [signUpTurnstileResetKey, setSignUpTurnstileResetKey] = useState(0);
  const [sessionMarker, setSessionMarker] = useState<null | string>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusMessageTone, setStatusMessageTone] = useState<FeedbackTone>("info");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorMethod, setTwoFactorMethod] = useState<null | "EMAIL" | "TOTP">(null);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyEmailTurnstileToken, setVerifyEmailTurnstileToken] = useState("");
  const [verifyEmailTurnstileResetKey, setVerifyEmailTurnstileResetKey] = useState(0);
  const [verifyToken, setVerifyToken] = useState(getInitialTokenFromUrl());
  const [organizationInvitationToken, setOrganizationInvitationToken] = useState(getInitialTokenFromUrl());
  const lastAutoActionTokenRef = useRef<null | string>(null);

  const signUpPasswordRules = useMemo(() => getPasswordRules(signUpPassword), [getPasswordRules, signUpPassword]);
  const newPasswordRules = useMemo(() => getPasswordRules(newPassword), [getPasswordRules, newPassword]);
  const signUpPasswordRulesMet = signUpPasswordRules.every((rule) => rule.met);
  const newPasswordRulesMet = newPasswordRules.every((rule) => rule.met);

  const clearAuthFlowState = useCallback(() => {
    setForgotEmail("");
    setNewPassword("");
    setResetToken("");
    setSignInPassword("");
    setTwoFactorCode("");
    setTwoFactorMethod(null);
    setVerifyEmail("");
    setVerifyToken("");
    setOrganizationInvitationToken("");
    setOrganizationInvitationPrompt(null);
    lastAutoActionTokenRef.current = null;
  }, []);

  const setMode = useCallback((mode: AuthMode) => {
    replaceUrlForMode(mode);
    setAuthMode(mode);
    clearAuthFlowState();
    setSignUpEmail("");
    setSignUpPassword("");
    setSignUpAcceptedTerms(false);
    setSignUpReceiveProductUpdates(false);
    setSignUpTurnstileToken("");
    setSignUpTurnstileResetKey((current) => current + 1);
    setForgotPasswordTurnstileToken("");
    setForgotPasswordTurnstileResetKey((current) => current + 1);
    setVerifyEmailTurnstileToken("");
    setVerifyEmailTurnstileResetKey((current) => current + 1);
    setFlowNotice(null);
    setStatusMessage("");
    setStatusMessageTone("info");
  }, [clearAuthFlowState]);

  const resetSignUpTurnstile = useCallback(() => {
    setSignUpTurnstileToken("");
    setSignUpTurnstileResetKey((current) => current + 1);
  }, []);

  const resetForgotPasswordTurnstile = useCallback(() => {
    setForgotPasswordTurnstileToken("");
    setForgotPasswordTurnstileResetKey((current) => current + 1);
  }, []);

  const resetVerifyEmailTurnstile = useCallback(() => {
    setVerifyEmailTurnstileToken("");
    setVerifyEmailTurnstileResetKey((current) => current + 1);
  }, []);

  const clearMessages = useCallback(() => {
    setStatusMessage("");
    setStatusMessageTone("info");
    setFlowNotice(null);
  }, []);

  const openSignIn = () => { setMode("sign-in"); setStatusMessage(""); };
  const openSignUp = () => { setMode("sign-up"); setStatusMessage(""); };
  const openForgotPassword = () => { setForgotEmail(signInEmail); setMode("forgot-password"); setStatusMessage(""); };
  const openAcceptancePopup = () => { setIsAcceptancePopupOpen(true); };
  const closeAcceptancePopup = () => { setIsAcceptancePopupOpen(false); };
  const openTermsOfUse = () => { setIsTermsOfUseOpen(true); };
  const closeTermsOfUse = () => { setIsTermsOfUseOpen(false); };

  const resetToSignedOutState = useCallback(() => {
    setAuthUser(null);
    setIsAcceptancePopupOpen(false);
    setLatestTermsOfUseAccepted(false);
    setSessionMarker(null);
    setIsUpcomingTermsBannerDismissed(false);
    setIsTermsOfUseOpen(false);
    setLatestTermsOfUseSummary(null);
    setMode("sign-in");
    setStatusMessage("");
    setStatusMessageTone("info");
  }, [setMode]);

  const showStatusMessage = useCallback((message: string, tone: FeedbackTone) => {
    setStatusMessage(message);
    setStatusMessageTone(tone);
  }, []);

  const isOrganizationInvitationMode = authMode === "organization-invitation-accept" || authMode === "organization-invitation-decline";
  const organizationInvitationAction = authMode === "organization-invitation-decline" ? "decline" : "accept";

  const resolveOrganizationInvitationResolvedMessage = useCallback((status: string) => {
    if (status === "ACCEPTED") {
      return t("admin.organizationInvitation.result.alreadyAccepted");
    }
    if (status === "DECLINED") {
      return t("admin.organizationInvitation.result.alreadyDeclined");
    }
    if (status === "EXPIRED") {
      return t("admin.organizationInvitation.result.expired");
    }
    if (status === "CANCELLED") {
      return t("admin.organizationInvitation.result.cancelled");
    }
    return t("admin.organizationInvitation.result.invalid");
  }, [t]);

  const closeOrganizationInvitationPrompt = useCallback(() => {
    setOrganizationInvitationPrompt(null);
    setOrganizationInvitationToken("");
    setMode("sign-in");
  }, [setMode]);
  const resolveAuthErrorMessage = useCallback((errorCode: AuthErrorCode | null | undefined, fallbackKey: TranslationKey) => {
    if (errorCode) {
      return t(AUTH_ERROR_TRANSLATION_KEYS[errorCode]);
    }
    return t(fallbackKey);
  }, [t]);

  const refreshAuthStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const { payload, response } = await fetchAdminAuthStatus();
      setLatestTermsOfUseAccepted(payload.latestTermsOfUseAccepted);
      if (!response.ok) {
        setAuthUser(null);
        setSessionMarker(null);
        setLatestTermsOfUseSummary(null);
        return false;
      }
      setAuthUser(payload.authenticated ? payload.user : null);
      setSessionMarker(payload.authenticated ? payload.sessionMarker : null);
      return payload.authenticated && !!payload.user;
    } catch {
      setAuthUser(null);
      setSessionMarker(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const withSubmitting = useCallback(async (action: () => Promise<void>) => {
    clearMessages();
    setIsSubmitting(true);
    try {
      await action();
    } catch {
      // Technical failures are surfaced by the shared request snackbar.
    } finally {
      setIsSubmitting(false);
    }
  }, [clearMessages]);

  useEffect(() => {
    registerUnauthorizedResponseHandler(resetToSignedOutState);
    return () => {
      registerUnauthorizedResponseHandler(null);
    };
  }, [resetToSignedOutState]);

  useEffect(() => {
    void refreshAuthStatus();
  }, [refreshAuthStatus]);

  useEffect(() => {
    if (!authUser || latestTermsOfUseAccepted) {
      setLatestTermsOfUseSummary(null);
      setIsUpcomingTermsBannerDismissed(false);
      return;
    }

    let cancelled = false;
    const loadLatestTermsSummary = async () => {
      try {
        const { payload } = await fetchLatestTermsOfUseSummary();
        if (!cancelled) {
          setLatestTermsOfUseSummary(payload);
          setIsUpcomingTermsBannerDismissed(false);
        }
      } catch {
        if (!cancelled) {
          setLatestTermsOfUseSummary(null);
        }
      }
    };

    void loadLatestTermsSummary();
    return () => {
      cancelled = true;
    };
  }, [authUser, latestTermsOfUseAccepted]);

  useEffect(() => {
    if (authMode !== "verify-email") {
      return;
    }

    const trimmedToken = verifyToken.trim();
    if (trimmedToken.length === 0 || lastAutoActionTokenRef.current === `verify:${trimmedToken}`) {
      return;
    }

    lastAutoActionTokenRef.current = `verify:${trimmedToken}`;
    void withSubmitting(async () => {
      const { payload, response } = await verifyAdminEmail({ token: trimmedToken });
      if (response.ok && payload.success) {
        setVerifyToken("");
        setFlowNotice({ message: t("admin.message.verifyEmailSuccess"), showBackToSignIn: true, tone: "success" });
        return;
      }
      setVerifyToken("");
      setFlowNotice({
        message: resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.verifyEmailInvalid"),
        showBackToSignIn: true,
        tone: "error"
      });
    });
  }, [authMode, resolveAuthErrorMessage, t, verifyToken, withSubmitting]);

  useEffect(() => {
    if (!authUser || !isOrganizationInvitationMode) {
      return;
    }

    const trimmedToken = organizationInvitationToken.trim();
    if (trimmedToken.length === 0) {
      setOrganizationInvitationPrompt({
        organizationName: null,
        action: organizationInvitationAction,
        canConfirm: false,
        isOpen: true,
        message: t("admin.organizationInvitation.result.invalid"),
        stage: "result",
        title: t("admin.organizationInvitation.title"),
        tone: "error",
      });
      return;
    }

    if (lastAutoActionTokenRef.current === `organization-invitation-preview:${authMode}:${trimmedToken}`) {
      return;
    }

    lastAutoActionTokenRef.current = `organization-invitation-preview:${authMode}:${trimmedToken}`;
    void withSubmitting(async () => {
      const { payload, response } = await previewAdminOrganizationInvitation({ token: trimmedToken });
      if (!response.ok) {
        setOrganizationInvitationPrompt({
          organizationName: null,
          action: organizationInvitationAction,
          canConfirm: false,
          isOpen: true,
          message: t("admin.organizationInvitation.result.invalid"),
          stage: "result",
          title: t("admin.organizationInvitation.title"),
          tone: "error",
        });
        return;
      }

      if (!payload.belongsToCurrentUser) {
        setOrganizationInvitationPrompt({
          organizationName: payload.organizationName,
          action: organizationInvitationAction,
          canConfirm: false,
          isOpen: true,
          message: t("admin.organizationInvitation.result.belongsToAnotherUser"),
          stage: "result",
          title: t("admin.organizationInvitation.title"),
          tone: "error",
        });
        return;
      }

      if (payload.status !== "ACTIVE") {
        setOrganizationInvitationPrompt({
          organizationName: payload.organizationName,
          action: organizationInvitationAction,
          canConfirm: false,
          isOpen: true,
          message: resolveOrganizationInvitationResolvedMessage(payload.status),
          stage: "result",
          title: t("admin.organizationInvitation.title"),
          tone: "error",
        });
        return;
      }

      setOrganizationInvitationPrompt({
        organizationName: payload.organizationName,
        action: organizationInvitationAction,
        canConfirm: true,
        isOpen: true,
        message: organizationInvitationAction === "accept"
          ? t("admin.organizationInvitation.confirm.accept")
          : t("admin.organizationInvitation.confirm.decline"),
        stage: "confirm",
        title: organizationInvitationAction === "accept"
          ? t("admin.organizationInvitation.title.accept")
          : t("admin.organizationInvitation.title.decline"),
        tone: "info",
      });
    });
  }, [
    organizationInvitationAction,
    organizationInvitationToken,
    authMode,
    authUser,
    isOrganizationInvitationMode,
    resolveOrganizationInvitationResolvedMessage,
    t,
    withSubmitting
  ]);

  useEffect(() => {
    if (authMode !== "forgot-password-reset") {
      return;
    }

    if (resetToken.trim().length > 0) {
      return;
    }

    setFlowNotice({ message: t("admin.message.passwordResetFailed"), showBackToSignIn: true, tone: "error" });
  }, [authMode, resetToken, t]);

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      const { payload, response } = await signInAdmin({ email: signInEmail, password: signInPassword });
      if (response.status === 403 && payload.emailVerificationRequired) {
        showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.emailVerificationRequired"), "error");
        setVerifyEmail(signInEmail);
        return;
      }
      if (!response.ok || !payload.authenticated || !payload.user) {
        if (response.ok && payload.twoFactorRequired) {
          setTwoFactorMethod(payload.twoFactorMethod);
          setAuthMode("two-factor");
          setTwoFactorCode("");
          return;
        }
        showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.invalidCredentials"), "error");
        return;
      }
      const isAuthenticated = await refreshAuthStatus();
      if (!isAuthenticated) { showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.invalidCredentials"), "error"); return; }
      setSignInPassword("");
    });
  };

  const handleTwoFactor = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      const { payload, response } = await completeAdminTwoFactor({ code: twoFactorCode });
      if (!response.ok || !payload.authenticated || !payload.user) {
        showStatusMessage(t("admin.auth.error.twoFactorInvalid"), "error");
        return;
      }
      const isAuthenticated = await refreshAuthStatus();
      if (!isAuthenticated) {
        showStatusMessage(t("admin.auth.error.twoFactorInvalid"), "error");
        return;
      }
      setTwoFactorCode("");
      setTwoFactorMethod(null);
    });
  };

  const handleSignUp = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      if (TURNSTILE_SITE_KEY && signUpTurnstileToken.trim().length === 0) {
        showStatusMessage(t("admin.auth.error.robotDetectionFailed"), "error");
        return;
      }
      const { payload: latestTerms } = await fetchLatestTermsOfUse();
      const { payload, response } = await signUpAdmin({
        acceptedTerms: signUpAcceptedTerms,
        email: signUpEmail,
        emailLanguage: language,
        name: signUpEmail,
        password: signUpPassword,
        receiveProductUpdates: signUpReceiveProductUpdates,
        termsOfUseUuid: latestTerms.uuid,
        turnstileToken: TURNSTILE_SITE_KEY ? signUpTurnstileToken : null,
      });
      if (TURNSTILE_SITE_KEY) {
        resetSignUpTurnstile();
      }
      if (response.status === 409 && payload.forgotPasswordAvailable) {
        setMode("forgot-password");
        setForgotEmail(signUpEmail);
        showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.userAlreadyExists"), "error");
        return;
      }
      if (response.status === 201 && payload.emailVerificationRequired) {
        replaceUrlForMode("sign-in");
        setAuthMode("sign-in");
        clearAuthFlowState();
        setFlowNotice({ message: t("admin.auth.message.checkEmail"), showBackToSignIn: false, tone: "success" });
        setVerifyEmail(signUpEmail);
        return;
      }
      if (!response.ok) { showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.signUpFailed"), "error"); }
    });
  };

  const requestPasswordReset = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      if (TURNSTILE_SITE_KEY && forgotPasswordTurnstileToken.trim().length === 0) {
        showStatusMessage(t("admin.auth.error.robotDetectionFailed"), "error");
        return;
      }
      const { payload, response } = await requestAdminPasswordReset({
        email: forgotEmail,
        turnstileToken: TURNSTILE_SITE_KEY ? forgotPasswordTurnstileToken : null,
      });
      if (TURNSTILE_SITE_KEY) {
        resetForgotPasswordTurnstile();
      }
      if (response.ok && payload.success) { setFlowNotice({ message: t("admin.message.forgotPasswordRequested"), showBackToSignIn: true, tone: "success" }); return; }
      showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.forgotPasswordRequestFailed"), "error");
    });
  };

  const resetPasswordWithToken = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      const { payload, response } = await resetAdminPassword({ newPassword, token: resetToken });
      if (response.ok && payload.success) {
        setMode("sign-in");
        setFlowNotice({ message: t("admin.message.passwordResetSuccess"), showBackToSignIn: true, tone: "success" });
        return;
      }
      showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.passwordResetInvalid"), "error");
    });
  };

  const verifyEmailToken = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      const { payload, response } = await verifyAdminEmail({ token: verifyToken });
      if (response.ok && payload.success) { setVerifyToken(""); setFlowNotice({ message: t("admin.message.verifyEmailSuccess"), showBackToSignIn: true, tone: "success" }); return; }
      showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.verifyEmailInvalid"), "error");
    });
  };

  const resendVerification = async (event: FormEvent) => {
    event.preventDefault();
    await withSubmitting(async () => {
      if (TURNSTILE_SITE_KEY && verifyEmailTurnstileToken.trim().length === 0) {
        showStatusMessage(t("admin.auth.error.robotDetectionFailed"), "error");
        return;
      }
      const { payload, response } = await resendAdminVerification({
        email: verifyEmail,
        turnstileToken: TURNSTILE_SITE_KEY ? verifyEmailTurnstileToken : null,
      });
      if (TURNSTILE_SITE_KEY) {
        resetVerifyEmailTurnstile();
      }
      if (response.ok && payload.success) { setFlowNotice({ message: t("admin.auth.message.resendVerificationSuccess"), showBackToSignIn: true, tone: "success" }); return; }
      showStatusMessage(resolveAuthErrorMessage(payload.errorCode, "admin.auth.error.resendVerificationFailed"), "error");
    });
  };

  const signOut = async () => {
    try {
      await signOutAdmin();
    } catch {
      // Technical failures are surfaced by the shared request snackbar.
    } finally {
      resetToSignedOutState();
    }
  };

  const acceptTermsOfUse = async () => {
    await withSubmitting(async () => {
      const { payload: latestTerms } = await fetchLatestTermsOfUse();
      const { payload, response } = await acceptLatestTermsOfUse({ uuid: latestTerms.uuid });
      if (!response.ok || !payload.success) {
        showStatusMessage(t("admin.auth.error.termsAcceptanceFailed"), "error");
        return;
      }
      closeAcceptancePopup();
      closeTermsOfUse();
      await refreshAuthStatus();
    });
  };

  const confirmOrganizationInvitation = async () => {
    const trimmedToken = organizationInvitationToken.trim();
    if (!organizationInvitationPrompt?.canConfirm || trimmedToken.length === 0) {
      return;
    }
    await withSubmitting(async () => {
      const request = { token: trimmedToken };
      const { payload, response } = organizationInvitationPrompt.action === "accept"
        ? await acceptAdminOrganizationInvitation(request)
        : await declineAdminOrganizationInvitation(request);
      if (response.ok && payload.success) {
        if (organizationInvitationPrompt.action === "accept") {
          onOrganizationInvitationAccepted();
        }
        setOrganizationInvitationPrompt({
          ...organizationInvitationPrompt,
          canConfirm: false,
          message: organizationInvitationPrompt.action === "accept"
            ? t("admin.organizationInvitation.result.accepted")
            : t("admin.organizationInvitation.result.declined"),
          stage: "result",
          title: t("admin.organizationInvitation.title"),
          tone: "success",
        });
        return;
      }
      setOrganizationInvitationPrompt({
        ...organizationInvitationPrompt,
        canConfirm: false,
        message: t("admin.organizationInvitation.result.expiredOrResolved"),
        stage: "result",
        title: t("admin.organizationInvitation.title"),
        tone: "error",
      });
    });
  };

  const panel: AdminAuthPanelModel = {
    actions: {
      acceptLatestTermsOfUse: acceptTermsOfUse,
      closeOrganizationInvitationPrompt,
      confirmOrganizationInvitation,
      handleSignIn,
      handleSignUp,
      handleTwoFactor,
      openAcceptancePopup,
      openForgotPassword,
      openSignIn,
      openSignUp,
      openTermsOfUse,
      requestPasswordReset,
      resendVerification,
      resetPasswordWithToken,
      setForgotPasswordTurnstileToken,
      setForgotEmail,
      setNewPassword,
      setResetToken,
      setSignInEmail,
      setSignInPassword,
      setSignUpAcceptedTerms,
      setSignUpEmail,
      setSignUpPassword,
      setSignUpReceiveProductUpdates,
      setSignUpTurnstileToken,
      resetSignUpTurnstile,
      setVerifyEmail,
      setVerifyEmailTurnstileToken,
      setVerifyToken,
      setTwoFactorCode,
      verifyEmailToken
    },
    organizationInvitationPrompt,
    authMode,
    flowNotice,
    forgotEmail,
    forgotPasswordTurnstileResetKey,
    forgotPasswordTurnstileSiteKey: TURNSTILE_SITE_KEY,
    forgotPasswordTurnstileToken,
    isSubmitting,
    newPassword,
    newPasswordRules,
    passwordPattern,
    passwordRequirements,
    resetSubmitDisabled: !newPasswordRulesMet || isSubmitting,
    resetToken,
    signInEmail,
    signInPassword,
    signUpAcceptedTerms,
    signUpEmail,
    signUpPassword,
    signUpPasswordRules,
    signUpReceiveProductUpdates,
    signUpSubmitDisabled: signUpEmail.trim().length === 0
      || !signUpPasswordRulesMet
      || !signUpAcceptedTerms
      || isSubmitting
      || (TURNSTILE_SITE_KEY.length > 0 && signUpTurnstileToken.trim().length === 0),
    signUpTurnstileResetKey,
    signUpTurnstileSiteKey: TURNSTILE_SITE_KEY,
    signUpTurnstileToken,
    statusMessage,
    statusMessageTone,
    twoFactorCode,
    twoFactorMethod,
    verifyEmail,
    verifyEmailTurnstileResetKey,
    verifyEmailTurnstileSiteKey: TURNSTILE_SITE_KEY,
    verifyEmailTurnstileToken,
    verifyToken
  };

  return {
    authUser,
    closeAcceptancePopup,
    closeTermsOfUse,
    isAcceptancePopupOpen,
    isLoading,
    isTermsOfUseOpen,
    isUpcomingTermsBannerDismissed,
    latestTermsOfUseAccepted,
    latestTermsOfUseSummary,
    panel,
    sessionMarker,
    setIsUpcomingTermsBannerDismissed,
    signOut,
    isOrganizationInvitationMode
  };
};
